import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import {
  Gift, Users, Copy, Share2, Trophy, Crown, Star,
  Loader2, Check, ChevronRight, Medal, Zap, Clock,
  TrendingUp, ExternalLink
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIER_COLORS = {
  Starter: 'from-slate-400 to-slate-500',
  Contributor: 'from-green-400 to-emerald-500',
  Influencer: 'from-blue-400 to-indigo-500',
  Champion: 'from-purple-400 to-violet-500',
  Legend: 'from-amber-400 to-orange-500',
  Ambassador: 'from-red-400 to-rose-500',
};

const ReferralsPage = () => {
  const [stats, setStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [applyCode, setApplyCode] = useState('');
  const [applying, setApplying] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, lbRes] = await Promise.all([
        axios.get(`${API}/referrals/stats`),
        axios.get(`${API}/referrals/leaderboard`).catch(() => ({ data: { leaderboard: [] } })),
      ]);
      setStats(statsRes.data);
      setLeaderboard(lbRes.data?.leaderboard || []);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCopy = () => {
    const code = stats?.referral_code;
    if (!code) return;
    navigator.clipboard.writeText(code);
    setCopied(true);
    toast.success('Referral code copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCopyLink = () => {
    const link = `${window.location.origin}/signup?ref=${stats?.referral_code}`;
    navigator.clipboard.writeText(link);
    toast.success('Referral link copied!');
  };

  const handleShare = (platform) => {
    const code = stats?.referral_code;
    const link = `${window.location.origin}/signup?ref=${code}`;
    const text = `Join me on iStudyPro and get 7 days of FREE premium! Use my referral code: ${code}`;
    const urls = {
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(link)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(text + ' ' + link)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(link)}`,
    };
    window.open(urls[platform], '_blank', 'width=600,height=400');
  };

  const handleApply = async () => {
    if (!applyCode.trim()) { toast.error('Enter a referral code'); return; }
    setApplying(true);
    try {
      const res = await axios.post(`${API}/referrals/claim`, { referral_code: applyCode.trim() });
      toast.success(res.data.message);
      setApplyCode('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply code');
    } finally {
      setApplying(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="referrals-loading">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  const totalRefs = stats?.total_referrals || 0;
  const currentTier = stats?.current_tier;
  const nextTier = stats?.next_tier;
  const progressPct = nextTier ? Math.round((totalRefs / nextTier.threshold) * 100) : 100;

  return (
    <div className="f500-page space-y-6" data-testid="referrals-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-[var(--isp-heading)] tracking-tight flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center">
              <Gift className="w-6 h-6 text-white" />
            </div>
            Referral Rewards
          </h1>
          <p className="text-[var(--isp-body)] mt-1">Invite friends and earn premium access</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-[var(--isp-glass-border)] pb-2">
        {[
          { id: 'dashboard', label: 'Dashboard', icon: TrendingUp },
          { id: 'tiers', label: 'Reward Tiers', icon: Crown },
          { id: 'leaderboard', label: 'Leaderboard', icon: Trophy },
          { id: 'apply', label: 'Apply Code', icon: Gift },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm ${
              activeTab === tab.id
                ? 'bg-pink-500/20 text-pink-400 border border-pink-500/30'
                : 'text-[var(--isp-body)] hover:text-[var(--isp-heading)]'
            }`}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" /> {tab.label}
          </button>
        ))}
      </div>

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && (
        <div className="space-y-6">
          {/* Referral Code Card */}
          <Card className="p-6  bg-gradient-to-br from-pink-500/10 to-rose-500/10 border border-pink-500/20" data-testid="referral-code-card">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex-1">
                <p className="text-sm text-[var(--isp-body)] mb-1">Your Referral Code</p>
                <div className="flex items-center gap-3">
                  <span className="text-3xl font-bold text-[var(--isp-heading)] font-mono tracking-wider" data-testid="referral-code">
                    {stats?.referral_code || '---'}
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={handleCopy}
                    className="text-[var(--isp-body)] hover:text-[var(--isp-heading)] rounded-lg"
                    data-testid="copy-code-btn"
                  >
                    {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleCopyLink}
                  className="rounded-xl border-[var(--isp-glass-border)] text-[var(--isp-body)] gap-2"
                  data-testid="copy-link-btn"
                >
                  <ExternalLink className="w-3 h-3" /> Copy Link
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleShare('whatsapp')}
                  className="rounded-xl bg-green-600 hover:bg-green-700 gap-1"
                  data-testid="share-whatsapp-btn"
                >
                  <Share2 className="w-3 h-3" /> Share
                </Button>
              </div>
            </div>

            {/* Social share buttons */}
            <div className="flex gap-2 mt-4 pt-4 border-t border-[var(--isp-glass-border)]">
              {[
                { id: 'twitter', label: 'X / Twitter' },
                { id: 'facebook', label: 'Facebook' },
                { id: 'linkedin', label: 'LinkedIn' },
              ].map(s => (
                <button
                  key={s.id}
                  onClick={() => handleShare(s.id)}
                  className="px-3 py-1.5 text-xs rounded-lg bg-[var(--isp-glass)] backdrop-blur-xl border border-[var(--isp-glass-border)] text-[var(--isp-body)] hover:text-[var(--isp-heading)] hover:border-white/20 transition-all"
                  data-testid={`share-${s.id}-btn`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </Card>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Total Referrals', value: totalRefs, icon: Users, color: 'from-blue-500 to-cyan-500' },
              { label: 'Premium Earned', value: `${stats?.rewards_earned?.premium_days || 0}d`, icon: Crown, color: 'from-purple-500 to-indigo-500' },
              { label: 'XP Earned', value: stats?.rewards_earned?.xp_earned || 0, icon: Zap, color: 'from-amber-500 to-orange-500' },
              { label: 'Current Tier', value: currentTier?.label || 'None', icon: Star, color: 'from-pink-500 to-rose-500' },
            ].map((s, i) => (
              <motion.div key={s.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                <Card className="p-4  bg-[var(--isp-glass)] backdrop-blur-xl border-[var(--isp-glass-border)]">
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${s.color} flex items-center justify-center mb-3`}>
                    <s.icon className="w-5 h-5 text-white" />
                  </div>
                  <p className="text-xl font-bold text-[var(--isp-heading)]">{s.value}</p>
                  <p className="text-xs text-[var(--isp-muted)] mt-0.5">{s.label}</p>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Tier Progress */}
          {nextTier && (
            <Card className="p-5  bg-[var(--isp-glass)] backdrop-blur-xl border-[var(--isp-glass-border)]" data-testid="tier-progress">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm text-[var(--isp-body)]">
                  Progress to <span className="text-[var(--isp-heading)] font-medium">{nextTier.label}</span>
                </p>
                <p className="text-sm text-[var(--isp-muted)]">
                  {totalRefs}/{nextTier.threshold} referrals
                </p>
              </div>
              <Progress value={progressPct} className="h-3 bg-white/10" />
              <p className="text-xs text-[var(--isp-muted)] mt-2">
                {stats?.referrals_to_next || 0} more referral{stats?.referrals_to_next !== 1 ? 's' : ''} to unlock
                <span className="text-purple-400"> +{nextTier.bonus_premium_days}d premium + {nextTier.bonus_xp} XP</span>
              </p>
            </Card>
          )}

          {/* Recent Referrals */}
          {stats?.referrals?.length > 0 && (
            <Card className="p-5  bg-[var(--isp-glass)] backdrop-blur-xl border-[var(--isp-glass-border)]" data-testid="recent-referrals">
              <h3 className="text-sm font-semibold text-[var(--isp-body)] mb-3 flex items-center gap-2">
                <Clock className="w-4 h-4 text-blue-400" /> Recent Referrals
              </h3>
              <div className="space-y-2">
                {stats.referrals.map((r, i) => (
                  <div key={`ref-${i}`} className="flex items-center gap-3 p-3 rounded-xl bg-slate-800/30 border border-[var(--isp-glass-border)]">
                    <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
                      <Check className="w-4 h-4 text-green-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-[var(--isp-heading)] truncate">{r.referee_name || 'User'}</p>
                      <p className="text-xs text-[var(--isp-muted)]">{r.created_at?.slice(0, 10)}</p>
                    </div>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/20 text-green-400">{r.status}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Tiers Tab */}
      {activeTab === 'tiers' && (
        <div className="space-y-3" data-testid="reward-tiers">
          {stats?.all_tiers?.map((tier, i) => {
            const isUnlocked = totalRefs >= tier.threshold;
            const gradient = TIER_COLORS[tier.label] || 'from-slate-400 to-slate-500';
            return (
              <motion.div
                key={tier.label}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.06 }}
              >
                <Card className={`p-5  border transition-all ${
                  isUnlocked
                    ? 'bg-gradient-to-r from-green-500/5 to-emerald-500/5 border-green-500/30'
                    : 'bg-[var(--isp-glass)] backdrop-blur-xl border-[var(--isp-glass-border)]'
                }`} data-testid={`tier-${tier.label.toLowerCase()}`}>
                  <div className="flex items-center gap-4">
                    <div className={`w-14 h-14  bg-gradient-to-br ${gradient} flex items-center justify-center flex-shrink-0 ${isUnlocked ? '' : 'opacity-40'}`}>
                      <Medal className="w-7 h-7 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className={`font-bold ${isUnlocked ? 'text-[var(--isp-heading)]' : 'text-[var(--isp-body)]'}`}>{tier.label}</h3>
                        {isUnlocked && <Check className="w-4 h-4 text-green-400" />}
                      </div>
                      <p className="text-xs text-[var(--isp-muted)]">{tier.threshold} referral{tier.threshold !== 1 ? 's' : ''} required</p>
                    </div>
                    <div className="text-right">
                      <p className={`font-bold ${isUnlocked ? 'text-purple-400' : 'text-[var(--isp-muted)]'}`}>
                        +{tier.bonus_premium_days}d
                      </p>
                      <p className="text-xs text-[var(--isp-muted)]">+{tier.bonus_xp} XP</p>
                    </div>
                  </div>
                </Card>
              </motion.div>
            );
          })}
          <p className="text-xs text-[var(--isp-muted)] text-center mt-4">
            Plus {stats?.rewards_earned?.premium_days ? '7' : '7'} days premium per referral for both you and your friend!
          </p>
        </div>
      )}

      {/* Leaderboard Tab */}
      {activeTab === 'leaderboard' && (
        <Card className="p-5  bg-[var(--isp-glass)] backdrop-blur-xl border-[var(--isp-glass-border)]" data-testid="referral-leaderboard">
          <h3 className="text-lg font-semibold text-[var(--isp-heading)] mb-4 flex items-center gap-2">
            <Trophy className="w-5 h-5 text-amber-400" /> Top Referrers
          </h3>
          {leaderboard.length === 0 ? (
            <p className="text-center text-[var(--isp-body)] py-8">No referral data yet. Be the first!</p>
          ) : (
            <div className="space-y-2">
              {leaderboard.map((entry, i) => (
                <div key={`step-${i}`} className={`flex items-center gap-3 p-3 rounded-xl ${
                  entry.is_you ? 'bg-pink-500/10 border border-pink-500/20' : 'bg-slate-800/30 border border-[var(--isp-glass-border)]'
                }`} data-testid={`ref-lb-${i}`}>
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center font-bold text-sm ${
                    i === 0 ? 'bg-yellow-500/20 text-yellow-400' :
                    i === 1 ? 'bg-gray-400/20 text-gray-300' :
                    i === 2 ? 'bg-orange-500/20 text-orange-400' :
                    'bg-white/10 text-[var(--isp-body)]'
                  }`}>
                    {i < 3 ? <Medal className="w-4 h-4" /> : `#${entry.rank}`}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[var(--isp-heading)] truncate">
                      {entry.name} {entry.is_you && <span className="text-xs text-pink-400">(You)</span>}
                    </p>
                    <p className="text-xs text-[var(--isp-muted)]">{entry.rewards_earned?.premium_days}d premium earned</p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-pink-400">{entry.total_referrals}</p>
                    <p className="text-[10px] text-[var(--isp-muted)]">referrals</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Apply Code Tab */}
      {activeTab === 'apply' && (
        <Card className="p-6  bg-[var(--isp-glass)] backdrop-blur-xl border-[var(--isp-glass-border)] max-w-md" data-testid="apply-code">
          <h3 className="text-lg font-semibold text-[var(--isp-heading)] mb-2 flex items-center gap-2">
            <Gift className="w-5 h-5 text-green-400" /> Apply a Referral Code
          </h3>
          <p className="text-sm text-[var(--isp-body)] mb-4">
            Got a code from a friend? Enter it below to get 7 days of free premium!
          </p>
          <div className="flex gap-2">
            <input
              value={applyCode}
              onChange={e => setApplyCode(e.target.value.toUpperCase())}
              placeholder="Enter referral code"
              className="flex-1 p-3 rounded-xl bg-[var(--isp-glass-inner)] border border-[var(--isp-glass-border)] text-white text-sm font-mono uppercase tracking-wider"
              data-testid="apply-code-input"
            />
            <Button
              onClick={handleApply}
              disabled={applying}
              className="rounded-xl gap-2 bg-gradient-to-r from-green-500 to-emerald-500"
              data-testid="apply-code-btn"
            >
              {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Apply
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
};

export default ReferralsPage;
